import React, { useState, Fragment, FormEvent } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface GenericListManagerProps<T> {
  candidate: any;
  setCandidate: (val: any) => void;
  dataKey: string;
  title: string;
  colorClass?: string;
  maxItems?: number;
  getLabel: (item: T) => string;
  getId: (item: T) => string;
  createItem: (input: string) => T;
}

export default function GenericListManager<T>({
  candidate,
  setCandidate,
  dataKey,
  title,
  colorClass = "bg-indigo-600",
  maxItems,
  getLabel,
  getId,
  createItem
}: GenericListManagerProps<T>) {

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [newValue, setNewValue] = useState<string>("");

  // ✅ SUPPORT BOTH ARRAY + OBJECT
  const isArrayMode = Array.isArray(candidate);

  const currentList: T[] = isArrayMode
    ? candidate
    : Array.isArray(candidate?.[dataKey])
    ? candidate[dataKey]
    : [];

  const isLimitReached = maxItems !== undefined && currentList.length >= maxItems;

  const closeModal = (): void => {
    setIsOpen(false);
    setNewValue("");
  };

  // const handleAdd = (e: FormEvent<HTMLFormElement>): void => {
  //   e.preventDefault();

  //   const trimmed = newValue.trim();
  //   if (!trimmed || isLimitReached) return;

  //   const newItem = createItem(trimmed);

  //   let updatedCandidate;

  //   // ✅ HANDLE BOTH MODES
  //   if (isArrayMode) {
  //     updatedCandidate = [...currentList, newItem];
  //   } else {
  //     updatedCandidate = {
  //       ...candidate,
  //       [dataKey]: [...currentList, newItem],
  //     };
  //   }

  //   setCandidate(updatedCandidate);
  //   closeModal();
  // };

  const removeItem = (id: string): void => {
    let updatedCandidate;

    const filtered = currentList.filter((item: T) => getId(item) !== id);

    // ✅ HANDLE BOTH MODES
    if (isArrayMode) {
      updatedCandidate = filtered;
    } else {
      updatedCandidate = {
        ...candidate,
        [dataKey]: filtered,
      };
    }

    setCandidate(updatedCandidate);
  };



  const handleAdd = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
  
    const trimmed = newValue.trim();
    if (!trimmed) return;
  
    // Split by comma if present
    const values = trimmed
      .split(",")
      .map(v => v.trim())
      .filter(v => v.length > 0);
  
    // Remove duplicates against existing values
    const existingLabels = currentList.map(item =>
      getLabel(item).trim().toLowerCase()
    );
  
    const uniqueValues = values.filter(
      value => !existingLabels.includes(value.toLowerCase())
    );
  
    // Check max limit
    if (
      maxItems !== undefined &&
      currentList.length + uniqueValues.length > maxItems
    ) {
      alert(`Only ${maxItems} ${title.toLowerCase()} allowed.`);
      return;
    }
  
    const newItems = uniqueValues.map(value => createItem(value));
  
    let updatedCandidate;
  
    if (isArrayMode) {
      updatedCandidate = [...currentList, ...newItems];
    } else {
      updatedCandidate = {
        ...candidate,
        [dataKey]: [...currentList, ...newItems],
      };
    }
  
    setCandidate(updatedCandidate);
    closeModal();
  };

  return (
    <section className="p-6 bg-white rounded-2xl border border-slate-100 shadow-sm mb-6 mt-7">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <div className={`w-1.5 h-6 ${colorClass} rounded-full`}></div>
          <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500">
            {title} {maxItems && `(${currentList.length}/${maxItems})`}
          </h3>
        </div>

        {!isLimitReached && (
          <button
            type="button"
            onClick={() => setIsOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 text-slate-600 rounded-lg text-xs font-bold hover:bg-slate-100 transition-colors border border-slate-200"
          >
            <PlusIcon className="w-3.5 h-3.5" /> ADD
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-3">
        {currentList.length > 0 ? (
          currentList.map((item) => (
            <div
              key={getId(item)}
              className={`flex items-center gap-2 px-4 py-2 ${colorClass} text-white rounded-xl font-semibold text-sm shadow-md`}
            >
              {getLabel(item)}
              <button
                type="button"
                onClick={() => removeItem(getId(item))}
                className="hover:bg-white/20 rounded-full p-0.5"
              >
                <XMarkIcon className="w-3.5 h-3.5" />
              </button>
            </div>
          ))
        ) : (
          <span className="text-sm text-slate-400 italic">
              No {title.toLowerCase()} added yet. You can add multiple entries using commas.
          </span>
        )}
      </div>

      {/* Modal */}
      <Transition show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[10000]" onClose={closeModal}>
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm" />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Dialog.Panel className="w-full max-w-2xl bg-white p-6 rounded-2xl shadow-xl">
              <Dialog.Title className="text-lg font-bold text-slate-900 mb-4">
                Add {title}
              </Dialog.Title>

              <form onSubmit={handleAdd} className="space-y-4">
                <input
                  type="text"
                  autoFocus
                  placeholder="Enter value..."
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-indigo-500 outline-none"
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                />

                <div className="flex gap-3 justify-end mt-6">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 text-sm font-semibold text-slate-600"
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className={`px-6 py-2 ${colorClass} text-white text-sm font-bold rounded-lg`}
                  >
                    Save
                  </button>
                </div>
              </form>
            </Dialog.Panel>
          </div>
        </Dialog>
      </Transition>
    </section>
  );
}